import { ThemeEvents } from '@theme/events';
import { getWishlist, toggleWishlist } from '@theme/wishlist';

/**
 * Delegated controller for every [data-wishlist-toggle] heart button on the page (product
 * cards, potentially many per PLP/collection grid). One listener rather than one custom element
 * per card — cheaper on grids with dozens of cards, and the interaction itself (toggle a
 * boolean) doesn't need per-instance state beyond what's already in the DOM/localStorage.
 */

/**
 * @param {HTMLElement} button
 * @returns {import('@theme/wishlist').WishlistItem}
 */
function readItem(button) {
  const { id, variantId, handle, url, title, image, price, comparePrice, requiresPersonalization } = button.dataset;
  return {
    id: /** @type {string} */ (id),
    variantId: /** @type {string} */ (variantId),
    handle: /** @type {string} */ (handle),
    url: /** @type {string} */ (url),
    title: /** @type {string} */ (title),
    image: /** @type {string} */ (image),
    price: /** @type {string} */ (price),
    comparePrice: comparePrice || undefined,
    requiresPersonalization: requiresPersonalization === 'true',
  };
}

/**
 * Syncs heart buttons' pressed/filled state to the current wishlist. The wishlist lives in
 * localStorage, so the server always renders every heart empty: this has to run on page load,
 * on every wishlist:update (so a card reflects removals made elsewhere, e.g. from the drawer),
 * and for every card added to the page later (filters, sorting, Constructor's grid, infinite
 * scroll, search results).
 * @param {ParentNode} [root]
 */
function syncButtons(root = document) {
  const buttons = [...root.querySelectorAll('[data-wishlist-toggle]')];
  if (root instanceof Element && root.matches('[data-wishlist-toggle]')) buttons.push(root);
  if (buttons.length === 0) return;

  const wishlistedIds = new Set(getWishlist().map((item) => String(item.id)));
  for (const button of buttons) {
    if (!(button instanceof HTMLElement)) continue;
    const id = button.dataset.id;
    if (!id) continue;
    const wishlisted = wishlistedIds.has(id);
    button.setAttribute('aria-pressed', String(wishlisted));
    const label = wishlisted ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  }
}

document.addEventListener(ThemeEvents.wishlistUpdate, () => syncButtons());

syncButtons();

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof Element) syncButtons(node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const button = target.closest('[data-wishlist-toggle]');
  if (!(button instanceof HTMLElement)) return;

  const id = button.dataset.id;
  if (!id) return;

  const nowWishlisted = toggleWishlist(id, readItem(button));

  if (nowWishlisted) {
    button.classList.add('wishlist-button--animating');
    button.addEventListener(
      'animationend',
      () => button.classList.remove('wishlist-button--animating'),
      { once: true }
    );
  }
});
